// Phase 5.3 — i18n engine. STATIC locale resources only — NEVER an LLM at runtime.
// - Nested dot-path keys (e.g. "nav.today").
// - {var} interpolation.
// - Pluralization via Intl.PluralRules (value = { one, other, few, many, zero, two }).
// - Missing keys fall back to English, then to the raw key; missing keys logged in dev.
import en from "@/lib/locales/en";
import es from "@/lib/locales/es";
import fr from "@/lib/locales/fr";
import de from "@/lib/locales/de";
import ar from "@/lib/locales/ar";
import { isRtl } from "@/lib/languages";

export const DEFAULT_LOCALE = "en";
export const DICTS = { en, es, fr, de, ar };

// Interface locales we actually ship a translated UI for (Phase 5.3 wave).
export const UI_LOCALES = [
  { code: "en", native: "English" },
  { code: "es", native: "Español" },
  { code: "fr", native: "Français" },
  { code: "de", native: "Deutsch" },
  { code: "ar", native: "العربية" },
];

const _warned = new Set();
const isDev = process.env.NODE_ENV !== "production";

function walk(dict, key) {
  return key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), dict);
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

// Core translate. locale → dot key → vars. Handles plural objects via {count}.
export function translate(locale, key, vars) {
  const loc = DICTS[locale] ? locale : DEFAULT_LOCALE;
  let val = walk(DICTS[loc], key);
  if (val === undefined && loc !== DEFAULT_LOCALE) val = walk(DICTS[DEFAULT_LOCALE], key);
  if (val === undefined) {
    if (isDev && !_warned.has(`${locale}:${key}`)) {
      _warned.add(`${locale}:${key}`);
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing key "${key}" (locale "${locale}")`);
    }
    return key; // last-resort: raw key (never "undefined"/blank)
  }
  // Arrays (e.g. conversation starters) pass through untouched.
  if (Array.isArray(val)) return val;
  // Plural object
  if (val && typeof val === "object") {
    const count = vars?.count;
    let cat = "other";
    try {
      cat = new Intl.PluralRules(loc).select(Number(count ?? 0));
    } catch { /* keep other */ }
    const chosen = val[cat] ?? val.other ?? val.one ?? Object.values(val)[0] ?? key;
    return interpolate(chosen, vars);
  }
  return interpolate(val, vars);
}

export const localeDir = (locale) => (isRtl(locale) ? "rtl" : "ltr");

// Locale-aware number & date formatting helpers.
export const formatNumber = (locale, n, opts) => {
  try { return new Intl.NumberFormat(locale, opts).format(n); } catch { return String(n); }
};
export const formatDate = (locale, d, opts) => {
  try { return new Intl.DateTimeFormat(locale, opts).format(new Date(d)); }
  catch { return String(d); }
};

// Coverage check (dev tooling, not a CMS): compares each locale's leaf keys to EN.
export function coverageReport() {
  const leaves = (obj, prefix = "") =>
    Object.entries(obj).flatMap(([k, v]) =>
      v && typeof v === "object" && !("one" in v || "other" in v)
        ? leaves(v, `${prefix}${k}.`)
        : [`${prefix}${k}`]);
  const canonical = leaves(en);
  const report = {};
  for (const code of Object.keys(DICTS)) {
    const keys = new Set(leaves(DICTS[code]));
    const missing = canonical.filter((k) => !keys.has(k));
    report[code] = { total: canonical.length, translated: canonical.length - missing.length, missing };
  }
  return report;
}
